import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import Card from "../Shared/Card/Card";
import PieChart from "../Graphs/PieChart";
import { usePersonEmails } from "../../data/people/usePersonEmails";
import { IEmail } from './../../models/email/Email';
import { Table } from "../Shared/Table/Table";
import { peopleQueryKeys } from "../../data/people/queryKeys";
import { createColumnHelper } from "@tanstack/react-table";
import formatDateTime from "../../utils/formatDateTime";
import { StyledTextInput } from "../Shared/StyledInputs/StyledTextInput";
import { useForm } from "react-hook-form";

function accumulateStats(statsArray: IEmail[] = []) {
  return statsArray.reduce(
    (acc, current) => {
      // delivered is a boolean on the object
      if (current && current.delivered) {
        acc.deliveredCount += 1;
      }

      const open = current && current.openCount != null ? Number(current.openCount) || 0 : 0;
      const click = current && current.clickCount != null ? Number(current.clickCount) || 0 : 0;

      acc.totalOpens += open;
      acc.totalClicks += click;
      acc.totalEmails += 1;

      return acc;
    },
    {
      totalEmails: 0,
      deliveredCount: 0,
      totalOpens: 0,
      totalClicks: 0,
    }
  );
}

interface OpensClicksChartProps {
  personId: string;
  date: Date;
}

function OpensClicksChart({ personId, date }: OpensClicksChartProps) {
  const { emails } = usePersonEmails(personId, date.toISOString());

  const stats = useMemo(() => {
    const raw = (emails && (emails.data?.data ?? emails.data)) as any;
    const list: IEmail[] = Array.isArray(raw) ? raw : [];
    return accumulateStats(list);
  }, [emails]);

  const chartData = {
    labels: ["Delivered", "Opens", "Clicks"],
    datasets: [
      {
        data: [stats.deliveredCount, stats.totalOpens, stats.totalClicks],
      },
    ],
  };

  return (
    <div>
      {Object.values(stats).some(value => value > 0) ? (
        <PieChart data={chartData} height={250} width="100%" />
      ) : (
        <></>
      )}
    </div>
  );
}

interface EmailCorrespondenceProps {
  personId: string;
}

function getYearMonthString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
}

const colsHelper = createColumnHelper<IEmail>();

export const EmailCorrespondence = ({ personId }: EmailCorrespondenceProps) => {
  const [date, setDate] = useState<Date>(new Date());
  const { register, handleSubmit, watch } = useForm<{ month: string; }>({
    defaultValues: { month: getYearMonthString(date) },
  });
  const debounceRef = useRef<number | null>(null);
  const initialMount = useRef(true);
  const watchedMonth = watch("month");

  const onSubmit = useCallback((data: { month?: string }) => {
    if (data.month) {
      const dateParts = data.month.split('-');
      const newDate = new Date();
      newDate.setFullYear(parseInt(dateParts[0]));
      newDate.setMonth(parseInt(dateParts[1]) - 1);

      setDate(newDate);
    }
  }, []);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [watchedMonth, handleSubmit, onSubmit]);


  const cols = [
    colsHelper.accessor("subject", {
      header: "Subject",
    }),
    colsHelper.accessor("sentDate", {
      header: "Sent At",
      cell: info => formatDateTime(info.getValue()),
    }),
    colsHelper.accessor("delivered", {
      header: "Delivered",
      cell: info => info.getValue() ? "Yes" : "No",
    }),
    colsHelper.accessor("openCount", {
      header: "Opens",
    }),
    colsHelper.accessor("clickCount", {
      header: "Clicks",
    }),
  ];


  return (
    // @ts-ignore
    <Card title="Email Correspondence" containerClassName="h-full" bodyClassName="h-full">
      <div className="p-4 pl-6">
        <form onSubmit={handleSubmit(onSubmit)} className="flex items-center space-x-4">
          <StyledTextInput
            label="View Statistics for"
            type="month"
            classes="w-96"
            registerName="month"
            register={register}
          />
        </form>
      </div>
      <OpensClicksChart personId={personId} date={date} />
      <div className="px-6 pb-6 pt-2">
        <Table<IEmail>
          url={`/person/${personId}/emails`}
          queryKey={peopleQueryKeys.emails(personId, date.toISOString())}
          queryString={{ date: date.toISOString() }}
          columns={cols}
          enablePagination={false}
          emptyMessage="No email correspondence for selected month."
          tableClasses="!mt-0"
        />
      </div>
    </Card>
  );
}